import { BARREL_LEN, MAX_HP, MAX_WIND, TANK_CENTER_DY, WATER_Y, WORLD_W, type Mine, type TerrainStyle } from '@tanky/shared';
import type { Camera } from './camera';
import type { Effects } from './effects';
import type { DisplayTank, ProjectileView } from './playback';
import { drawTank, emblemImage } from './tankArt';
import type { Leaves } from './leaves';

/** Od tohoto přiblížení se tanky kreslí detailně (včetně obrázku). */
export const DETAIL_ZOOM = 1.5;

export interface EmoteState {
  blinkUntil: number;
  hopAt: number;
  waveUntil: number;
}

export interface Bubble {
  text: string;
  start: number;
  until: number;
}

export interface Scene {
  style: TerrainStyle;
  terrain: HTMLCanvasElement;
  tanks: DisplayTank[];
  currentId: string | null;
  meId: string;
  projectiles: ProjectileView[];
  effects: Effects;
  aim: { tank: DisplayTank; angle: number; power: number; mine: boolean } | null;
  time: number;
  emotes: Map<string, EmoteState>;
  bubbles: Map<string, Bubble>;
  windsock: { x: number; y: number; wind: number } | null;
  mines: Mine[];
  wind: number;
  leaves: Leaves;
  /** Průzkumné drony ve vzduchu. */
  drones?: { x: number; y: number; color: string }[];
  /** Zaměřovač bombometčíka při letu bombardéru. */
  bombsight?: { x: number; y: number; planeX: number; planeY: number; bombsLeft: number } | null;
}

export const SKY: Record<TerrainStyle, [string, string, string]> = {
  hills: ['#18203d', '#3b3f6e', '#e59366'],
  caves: ['#0f1022', '#262447', '#6a4c7c'],
  moon: ['#000000', '#04050b', '#0d0f18'],
  mars: ['#2a1612', '#7a3e26', '#d6935f'],
};

/** Barva a vzhled „vody“ – na Měsíci a Marsu je to propast / prachový opar. */
const ABYSS: Record<TerrainStyle, [string, string]> = {
  hills: ['rgba(70,150,220,0.85)', 'rgba(20,50,110,0.95)'],
  caves: ['rgba(70,150,220,0.85)', 'rgba(20,50,110,0.95)'],
  moon: ['rgba(20,20,26,0.9)', 'rgba(0,0,0,1)'],
  mars: ['rgba(150,70,40,0.75)', 'rgba(50,20,12,0.98)'],
};

// Pevné hvězdy (deterministicky, ať neblikají)
const STARS = Array.from({ length: 70 }, (_, i) => ({
  x: ((i * 7919) % 1000) / 1000,
  y: ((i * 104729) % 1000) / 1000,
  s: (i % 3) + 1,
}));

export function drawScene(ctx: CanvasRenderingContext2D, cam: Camera, dpr: number, s: Scene) {
  const { vw, vh } = cam;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Obloha (paralaxa: posouvá se jen trochu)
  const [c0, c1, c2] = SKY[s.style];
  const g = ctx.createLinearGradient(0, 0, 0, vh);
  g.addColorStop(0, c0);
  g.addColorStop(0.55, c1);
  g.addColorStop(1, c2);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, vw, vh);
  if (s.style === 'moon') drawEarth(ctx, vw, vh, cam.x);
  if (s.style === 'mars') drawSmallSun(ctx, vw, vh, cam.x);
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  // Na Měsíci hvězdy neblikají (není atmosféra) a je jich vidět víc
  const starAlpha = s.style === 'moon' ? 1 : s.style === 'mars' ? 0.35 : 0.7;
  for (const st of STARS) {
    const x = (st.x * vw * 1.3 - cam.x * 0.05) % vw;
    const y = st.y * vh * 0.55;
    const tw = s.style === 'moon' ? 0.9 : 0.4 + 0.6 * Math.abs(Math.sin(s.time * 0.8 + st.x * 20));
    ctx.globalAlpha = tw * starAlpha;
    ctx.fillRect(x < 0 ? x + vw : x, y, st.s * 0.8, st.s * 0.8);
  }
  ctx.globalAlpha = 1;

  // Listí na pozadí (za terénem)
  s.leaves.draw(ctx);

  // Svět
  const shake = s.effects.shakeAmount;
  const shakeX = shake ? (Math.random() - 0.5) * shake : 0;
  const shakeY = shake ? (Math.random() - 0.5) * shake : 0;
  const z = cam.zoom * dpr;
  ctx.setTransform(z, 0, 0, z, (vw / 2 - cam.x * cam.zoom + shakeX) * dpr, (vh / 2 - cam.y * cam.zoom + shakeY) * dpr);

  ctx.imageSmoothingEnabled = cam.zoom < 1;
  ctx.drawImage(s.terrain, 0, 0);

  if (s.aim) drawAim(ctx, s.aim.tank, s.aim.angle, s.aim.power, s.aim.mine, s.time);

  if (s.windsock) drawWindsock(ctx, s.windsock.x, s.windsock.y, s.windsock.wind, s.time);

  for (const m of s.mines) drawMine(ctx, m, m.owner === s.meId, s.time);

  for (const t of s.tanks) {
    if (t.target) {
      if (!t.alive) continue;
      drawTarget(ctx, t.x, t.y, true, s.time);
      continue;
    }
    const e = s.emotes.get(t.id);
    const hopT = e ? s.time - e.hopAt : Infinity;
    const hop = hopT >= 0 && hopT < 0.9 ? Math.abs(Math.sin((hopT / 0.9) * Math.PI * 2)) * 14 * (1 - hopT / 0.9) : 0;
    const blink = !!e && s.time < e.blinkUntil && Math.floor(s.time * 8) % 2 === 0;
    const angle = e && s.time < e.waveUntil ? 90 + Math.sin(s.time * 14) * 40 : t.angle;
    if (t.id === s.currentId && t.alive) drawCurrentMarker(ctx, t.x, t.y - hop, s.time);
    const detail = cam.zoom >= DETAIL_ZOOM;
    drawTank(ctx, t.x, t.y, angle, t.look, t.color, {
      alive: t.alive,
      blink,
      hop,
      recoil: t.recoil,
      detail,
      emblem: detail ? emblemImage(t.id, t.look) : null,
      wind: s.wind,
      time: s.time,
    });
  }

  for (const p of s.projectiles) drawProjectile(ctx, p, s.time);
  for (const d of s.drones ?? []) drawDrone(ctx, d.x, d.y, d.color, s.time);
  if (s.bombsight) drawBombsight(ctx, s.bombsight, s.time);

  s.effects.draw(ctx);

  // Voda
  const wy = WATER_Y + 4;
  const wg = ctx.createLinearGradient(0, wy, 0, wy + 120);
  const [a0, a1] = ABYSS[s.style];
  wg.addColorStop(0, a0);
  wg.addColorStop(1, a1);
  const wave = s.style === 'moon' ? 0 : 1;
  ctx.fillStyle = wg;
  ctx.beginPath();
  ctx.moveTo(-2000, wy + 4000);
  for (let x = -2000; x <= WORLD_W + 2000; x += 20) {
    ctx.lineTo(x, wy + (Math.sin(x * 0.03 + s.time * 2) * 3 + Math.sin(x * 0.011 - s.time) * 2) * wave);
  }
  ctx.lineTo(WORLD_W + 2000, wy + 4000);
  ctx.closePath();
  ctx.fill();

  // Popisky v obrazovkových souřadnicích (ostré při každém zoomu)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.textAlign = 'center';
  for (const t of s.tanks) {
    if (!t.alive) continue;
    const p = cam.toScreen(t.x, t.y - 22);
    ctx.font = `600 11px system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillText(t.name, p.x + 1, p.y - 7);
    ctx.fillStyle = t.id === s.meId ? '#ffe08a' : '#fff';
    ctx.fillText(t.name, p.x, p.y - 8);
    const w = 30;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(p.x - w / 2 - 1, p.y - 3, w + 2, 5);
    const k = Math.max(0, t.hp / MAX_HP);
    ctx.fillStyle = k > 0.5 ? '#4cd18a' : k > 0.25 ? '#ffc048' : '#ff5c6c';
    ctx.fillRect(p.x - w / 2, p.y - 2, w * k, 3);
  }
  for (const t of s.tanks) {
    const b = s.bubbles.get(t.id);
    if (!b || s.time > b.until || !t.alive) continue;
    const p = cam.toScreen(t.x, t.y - 22);
    const alpha = Math.min(1, (b.until - s.time) / 0.4, (s.time - b.start) / 0.15);
    drawBubble(ctx, p.x, p.y - 22, b.text, Math.max(0, alpha));
  }
  for (const f of s.effects.floaters) {
    const p = cam.toScreen(f.x, f.y);
    ctx.globalAlpha = Math.min(1, 1.6 - f.life);
    ctx.font = `800 16px system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillText(f.text, p.x + 1, p.y + 1);
    ctx.fillStyle = f.color;
    ctx.fillText(f.text, p.x, p.y);
  }
  ctx.globalAlpha = 1;

  // Záblesk atomovky
  if (s.effects.flash > 0) {
    ctx.fillStyle = `rgba(255,250,235,${Math.min(0.85, s.effects.flash)})`;
    ctx.fillRect(0, 0, vw, vh);
  }
}

function drawCurrentMarker(ctx: CanvasRenderingContext2D, x: number, y: number, time: number) {
  const bob = Math.sin(time * 5) * 2;
  ctx.fillStyle = '#ffe08a';
  ctx.beginPath();
  ctx.moveTo(x, y - 36 + bob);
  ctx.lineTo(x - 5, y - 43 + bob);
  ctx.lineTo(x + 5, y - 43 + bob);
  ctx.closePath();
  ctx.fill();
}

/** Průzkumný dron: tělo, čtyři rotory (bliknou) a světlo. */
function drawDrone(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, time: number) {
  ctx.save();
  ctx.translate(x, y + Math.sin(time * 5) * 1.2);
  ctx.strokeStyle = '#1d1d24';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-7, -2);
  ctx.lineTo(7, -2);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillRect(-3.5, -3.5, 7, 4);
  ctx.fillStyle = `rgba(220,220,230,${Math.floor(time * 20) % 2 ? 0.7 : 0.35})`;
  ctx.fillRect(-10, -4.5, 6, 1.2);
  ctx.fillRect(4, -4.5, 6, 1.2);
  ctx.fillStyle = Math.floor(time * 2) % 2 ? '#ff4d4d' : '#66ccff';
  ctx.fillRect(-0.8, 0.5, 1.6, 1.6);
  ctx.restore();
}

/** Země na měsíční obloze (modrá koule s mraky, osvětlená z boku). */
function drawEarth(ctx: CanvasRenderingContext2D, vw: number, vh: number, camX: number) {
  const r = Math.min(vw, vh) * 0.07;
  const x = vw * 0.78 - camX * 0.02;
  const y = vh * 0.2;
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r);
  g.addColorStop(0, '#9fd3ff');
  g.addColorStop(0.55, '#2f79c9');
  g.addColorStop(1, '#0d2a55');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  // Pevnina a mraky
  ctx.fillStyle = 'rgba(96,160,80,0.75)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.25, y + r * 0.1, r * 0.35, r * 0.22, 0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.beginPath();
  ctx.ellipse(x + r * 0.2, y - r * 0.3, r * 0.4, r * 0.1, -0.3, 0, Math.PI * 2);
  ctx.fill();
  // Noční strana
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.beginPath();
  ctx.arc(x, y, r, -Math.PI / 2 + 0.3, Math.PI / 2 + 0.3);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = '#7fbfff';
  ctx.beginPath();
  ctx.arc(x, y, r * 1.12, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** Na Marsu je Slunce menší a bledší. */
function drawSmallSun(ctx: CanvasRenderingContext2D, vw: number, vh: number, camX: number) {
  const x = vw * 0.22 - camX * 0.02;
  const y = vh * 0.28;
  const r = Math.min(vw, vh) * 0.025;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r * 5);
  g.addColorStop(0, 'rgba(255,245,225,0.95)');
  g.addColorStop(0.2, 'rgba(210,225,255,0.5)');
  g.addColorStop(1, 'rgba(210,225,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r * 5, 0, Math.PI * 2);
  ctx.fill();
}

function drawBubble(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, alpha: number) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = '600 13px system-ui, sans-serif';
  const w = Math.min(240, ctx.measureText(text).width) + 16;
  const h = 24;
  const bx = x - w / 2;
  const by = y - h;
  ctx.fillStyle = 'rgba(255,255,255,0.96)';
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(bx, by, w, h, 10);
  ctx.moveTo(x - 6, by + h - 0.5);
  ctx.lineTo(x, by + h + 7);
  ctx.lineTo(x + 6, by + h - 0.5);
  ctx.fill();
  ctx.stroke();
  // Překrýt čáru mezi bublinou a ocáskem
  ctx.fillRect(x - 5.5, by + h - 2, 11, 2.5);
  ctx.fillStyle = '#1b2033';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, by + h / 2 + 0.5, 224);
  ctx.restore();
}

/** Anemoskop (větrný rukáv) stojící na povrchu v bodě (x, y). */
function drawWindsock(ctx: CanvasRenderingContext2D, x: number, y: number, wind: number, time: number) {
  const poleH = 42;
  const top = y - poleH;
  ctx.strokeStyle = '#3a3d48';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y + 2);
  ctx.lineTo(x, top - 2);
  ctx.stroke();
  ctx.fillStyle = '#c9ccd6';
  ctx.beginPath();
  ctx.arc(x, top - 3, 2, 0, Math.PI * 2);
  ctx.fill();

  const k = Math.min(1, Math.abs(wind) / MAX_WIND);
  const dir = wind < 0 ? -1 : 1;
  // Úhel od svislice: bezvětří = svěšený rukáv, silný vítr = vodorovně
  const flutter = Math.sin(time * (3 + k * 6)) * (0.06 + k * 0.08);
  const segs = 5;
  const segLen = 5.2;
  let px = x;
  let py = top;
  let r0 = 4.2;
  for (let i = 0; i < segs; i++) {
    const sag = (1 - k) * (0.9 + i * 0.12) + k * 0.05 * i;
    // ang = úhel od svislice dolů (0 = visí, π/2 = vodorovně po větru)
    const ang = (Math.PI / 2) * (1 - Math.min(1, sag)) + flutter * (i + 1);
    const dx = Math.sin(ang) * segLen * dir;
    const dy = Math.cos(ang) * segLen;
    const r1 = r0 * 0.84;
    ctx.fillStyle = i % 2 === 0 ? '#f05a3c' : '#f4f1ea';
    ctx.beginPath();
    // Lichoběžník kolmo na směr segmentu
    const len = Math.hypot(dx, dy) || 1;
    const ox = -dy / len;
    const oy = dx / len;
    ctx.moveTo(px + ox * r0, py + oy * r0);
    ctx.lineTo(px + dx + ox * r1, py + dy + oy * r1);
    ctx.lineTo(px + dx - ox * r1, py + dy - oy * r1);
    ctx.lineTo(px - ox * r0, py - oy * r0);
    ctx.closePath();
    ctx.fill();
    px += dx;
    py += dy;
    r0 = r1;
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, top - 4.2);
  ctx.lineTo(x, top + 4.2);
  ctx.stroke();
}

function trailPath(ctx: CanvasRenderingContext2D, p: ProjectileView) {
  ctx.beginPath();
  ctx.moveTo(p.trail[0], p.trail[1]);
  for (let i = 2; i < p.trail.length; i += 2) ctx.lineTo(p.trail[i], p.trail[i + 1]);
  ctx.lineTo(p.x, p.y);
}

function ball(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(${rgb},${a})`);
  g.addColorStop(1, `rgba(${rgb},0)`);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function drawProjectile(ctx: CanvasRenderingContext2D, p: ProjectileView, time: number) {
  const { x, y } = p;
  if (p.weapon === 'laser') {
    if (p.trail.length < 2) return;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.globalCompositeOperation = 'lighter';
    trailPath(ctx, p);
    ctx.strokeStyle = 'rgba(255,40,80,0.35)';
    ctx.lineWidth = 7;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,120,150,0.9)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();
    glow(ctx, x, y, 14, '255,80,120', 0.9);
    ctx.restore();
    return;
  }
  if (p.weapon === 'flame') {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const f = 0.75 + 0.25 * Math.sin(time * 30 + x);
    glow(ctx, x, y - 3, 18 * f, '255,110,20', 0.75);
    glow(ctx, x, y - 4, 7 * f, '255,230,140', 1);
    ctx.restore();
    return;
  }
  // Stopa
  if (p.trail.length >= 4 && p.weapon !== 'roller' && p.weapon !== 'bomberplane') {
    trailPath(ctx, p);
    if (p.weapon === 'bazuka') {
      ctx.strokeStyle = 'rgba(235,240,250,0.65)';
      ctx.lineWidth = 2.6;
    } else {
      ctx.strokeStyle = p.weapon === 'teleport' ? 'rgba(200,125,255,0.5)' : p.weapon === 'airstrike' ? 'rgba(255,90,70,0.45)' : 'rgba(255,230,180,0.35)';
      ctx.lineWidth = 1.5;
    }
    ctx.stroke();
  }
  const heading = Math.atan2(p.vy, p.vx);
  switch (p.weapon) {
    case 'bomberplane':
      drawBomberPlane(ctx, x, y, p.vx, time);
      return;
    case 'bomber':
      drawBomberBomb(ctx, x, y, heading);
      return;
    case 'bazuka': {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(heading);
      // Plamen a dým z trysky
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const fl = 0.8 + 0.4 * Math.sin(time * 50 + x);
      glow(ctx, -10, 0, 11 * fl, '255,160,50', 0.85);
      ctx.fillStyle = 'rgba(255,240,180,0.95)';
      ctx.beginPath();
      ctx.moveTo(-6, -1.8);
      ctx.lineTo(-6 - 10 * fl, 0);
      ctx.lineTo(-6, 1.8);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      // Tělo rakety, bojová hlavice a křidélka
      ctx.fillStyle = '#4a533a';
      ctx.beginPath();
      ctx.roundRect(-7, -2.2, 11, 4.4, 1.5);
      ctx.fill();
      // Červená špička (hlavice)
      ctx.fillStyle = '#e83e2e';
      ctx.beginPath();
      ctx.moveTo(4, -2.2);
      ctx.lineTo(9, 0);
      ctx.lineTo(4, 2.2);
      ctx.closePath();
      ctx.fill();
      // Stabilizační křidélka vzadu
      ctx.fillStyle = '#2c3325';
      ctx.fillRect(-7, -4.5, 3.5, 2.3);
      ctx.fillRect(-7, 2.2, 3.5, 2.3);
      ctx.restore();
      return;
    }
    case 'nuke':
      glow(ctx, x, y, 16, '255,220,60', 0.55);
      ball(ctx, x, y, 5, '#ffd23f');
      ctx.fillStyle = '#222';
      for (let k = 0; k < 3; k++) {
        const a0 = time * 6 + (k * Math.PI * 2) / 3;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.arc(x, y, 4, a0, a0 + 0.7);
        ctx.closePath();
        ctx.fill();
      }
      return;
    case 'airbomb': {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(heading);
      ctx.fillStyle = '#3d4450';
      ctx.beginPath();
      ctx.ellipse(0, 0, 6, 2.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#6b7383';
      ctx.fillRect(-7, -3, 2, 6);
      ctx.restore();
      return;
    }
    case 'airstrike':
      glow(ctx, x, y, 9, '255,70,60', 0.6);
      ball(ctx, x, y, 2.6, '#ff5c4d');
      return;
    case 'banana': {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(p.age * 12);
      const s2 = p.bomblet ? 1 : 1.4;
      ctx.scale(s2, s2);
      ctx.fillStyle = '#ffe135';
      ctx.strokeStyle = 'rgba(80,60,0,0.8)';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(0, -2, 5, Math.PI * 0.15, Math.PI * 0.85);
      ctx.arc(0, -4.5, 5.5, Math.PI * 0.8, Math.PI * 0.2, true);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      return;
    }
    case 'roller': {
      ball(ctx, x, y - 3, 4, '#8d939e');
      const a = (p.x / 4) % (Math.PI * 2);
      ctx.strokeStyle = '#2d3038';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x - Math.cos(a) * 4, y - 3 - Math.sin(a) * 4);
      ctx.lineTo(x + Math.cos(a) * 4, y - 3 + Math.sin(a) * 4);
      ctx.stroke();
      return;
    }
    case 'teleport':
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      glow(ctx, x, y, 12, '200,125,255', 0.8);
      for (let k = 0; k < 4; k++) {
        const a = time * 8 + k * 1.57;
        ctx.fillStyle = '#f3d9ff';
        ctx.fillRect(x + Math.cos(a) * 7 - 0.8, y + Math.sin(a) * 7 - 0.8, 1.6, 1.6);
      }
      ctx.restore();
      ball(ctx, x, y, 2.6, '#c77dff');
      return;
    case 'kamikaze': {
      drawDrone(ctx, x, y, '#e74c3c', time);
      return;
    }
    case 'guided': {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(heading);
      // Plamen z trysky
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const fl = 0.7 + 0.3 * Math.sin(time * 40 + x);
      glow(ctx, -9, 0, 9 * fl, '255,140,40', 0.8);
      ctx.fillStyle = 'rgba(255,230,160,0.95)';
      ctx.beginPath();
      ctx.moveTo(-6, -1.6);
      ctx.lineTo(-6 - 8 * fl, 0);
      ctx.lineTo(-6, 1.6);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      // Tělo, hrot a křidélka
      ctx.fillStyle = '#c9ced8';
      ctx.beginPath();
      ctx.roundRect(-6, -2, 11, 4, 2);
      ctx.fill();
      ctx.fillStyle = '#e8412c';
      ctx.beginPath();
      ctx.moveTo(5, -2);
      ctx.lineTo(9, 0);
      ctx.lineTo(5, 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#6b7383';
      ctx.fillRect(-6, -4, 3, 2);
      ctx.fillRect(-6, 2, 3, 2);
      ctx.restore();
      return;
    }
    case 'pellet':
      ball(ctx, x, y, 1.6, '#e8e3d0');
      return;
    case 'bunker': {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(heading);
      ctx.fillStyle = '#5b6170';
      ctx.fillRect(-7, -2.6, 9, 5.2);
      ctx.fillStyle = '#c0c6d2';
      ctx.beginPath();
      ctx.moveTo(2, -2.6);
      ctx.lineTo(8, 0);
      ctx.lineTo(2, 2.6);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#2d3038';
      ctx.lineWidth = 0.8;
      for (let i = -5; i < 2; i += 2.2) {
        const o = (time * 30) % 2.2;
        ctx.beginPath();
        ctx.moveTo(i + o, -2.6);
        ctx.lineTo(i + o + 1.2, 2.6);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    case 'sheep': {
      const bob = Math.abs(Math.sin(p.age * 14)) * 1.5;
      const dir = p.vx < 0 ? -1 : 1;
      ctx.save();
      ctx.translate(x, y - 5 - bob);
      ctx.scale(dir, 1);
      ctx.fillStyle = '#2b2b2b';
      for (const lx of [-3, 3]) ctx.fillRect(lx - 0.6, 2, 1.2, 3 + bob);
      ctx.fillStyle = '#f4f1ea';
      for (const [cx, cy, r] of [[-3, 0, 3], [0, -1.5, 3.2], [3, 0, 3], [0, 1, 3]] as const) {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = '#2b2b2b';
      ctx.beginPath();
      ctx.ellipse(5.5, -1.5, 2, 1.6, 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillRect(6, -2.3, 0.7, 0.7);
      ctx.restore();
      return;
    }
    case 'mine':
      drawMine(ctx, { id: 0, x, y: y + 2, owner: '', armedTurn: 0 }, false, time);
      return;
    case 'quake':
      ball(ctx, x, y, 3.5, '#8d6e63');
      return;
    case 'blackhole':
      glow(ctx, x, y, 10, '179,136,255', 0.6);
      ball(ctx, x, y, 3.2, '#120a1e');
      return;
    case 'lightning':
      glow(ctx, x, y, 9, '255,240,120', 0.7);
      ball(ctx, x, y, 2.6, '#fff27a');
      return;
    case 'napalm':
      glow(ctx, x, y, 9, '255,120,30', 0.6);
      ball(ctx, x, y, 3, '#ff7b25');
      return;
  }
  const r = p.weapon === 'heavy' ? 4 : p.bomblet ? 2 : 3;
  ball(ctx, x, y, r, p.weapon === 'dirt' ? '#a0703f' : p.weapon === 'grenade' ? '#5f8f3a' : '#f5f5f5');
}

function drawAim(ctx: CanvasRenderingContext2D, t: DisplayTank, angle: number, power: number, mine: boolean, time: number) {
  const a = (angle * Math.PI) / 180;
  const cx = t.x;
  const cy = t.y - TANK_CENTER_DY - 2;
  const len = 24 + power * 1.3;
  const dx = Math.cos(a);
  const dy = -Math.sin(a);
  ctx.strokeStyle = mine ? 'rgba(255,224,138,0.9)' : 'rgba(255,255,255,0.45)';
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 5]);
  ctx.lineDashOffset = -time * 20;
  ctx.beginPath();
  ctx.moveTo(cx + dx * (BARREL_LEN + 4), cy + dy * (BARREL_LEN + 4));
  ctx.lineTo(cx + dx * len, cy + dy * len);
  ctx.stroke();
  ctx.setLineDash([]);
  const ex = cx + dx * len;
  const ey = cy + dy * len;
  ctx.beginPath();
  ctx.arc(ex, ey, 6, 0, Math.PI * 2);
  ctx.moveTo(ex - 9, ey);
  ctx.lineTo(ex + 9, ey);
  ctx.moveTo(ex, ey - 9);
  ctx.lineTo(ex, ey + 9);
  ctx.stroke();
}

/** Cvičný terč (trénink): červenobílé kruhy na stojanu (zničený zmizí z mapy). */
function drawTarget(ctx: CanvasRenderingContext2D, x: number, y: number, alive: boolean, time: number) {
  if (!alive) return;
  ctx.save();
  // Stojan
  ctx.strokeStyle = '#6b4a2f';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(x - 4, y);
  ctx.lineTo(x, y - 4);
  ctx.lineTo(x + 4, y);
  ctx.stroke();
  // Střed terče = zásahový střed tanku (y − 6)
  const cy = y - 7 + Math.sin(time * 2 + x) * 0.3;
  const rings = ['#e74c3c', '#fff', '#e74c3c', '#fff', '#e74c3c'];
  rings.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(x, cy, 7 - i * 1.4, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.arc(x, cy, 7, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Mina: tmavý disk s blikající diodou (vlastní zelená, cizí červená). */
function drawMine(ctx: CanvasRenderingContext2D, m: Mine, mine: boolean, time: number) {
  const x = m.x;
  const y = m.y + 1;
  ctx.fillStyle = '#2a2d35';
  ctx.beginPath();
  ctx.ellipse(x, y - 1.5, 5, 2.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#4a4f5c';
  ctx.beginPath();
  ctx.ellipse(x, y - 2.5, 3.4, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();
  const on = Math.floor(time * 2 + m.id * 0.37) % 2 === 0;
  if (on) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    glow(ctx, x, y - 3.5, 5, mine ? '76,209,138' : '255,70,70', 0.9);
    ctx.restore();
  }
  ctx.fillStyle = on ? (mine ? '#7dffb0' : '#ff6b6b') : '#5a1f1f';
  ctx.fillRect(x - 0.7, y - 4.3, 1.4, 1.4);
}

/** Těžký bombardér: trup, velká křídla, motory s točícími se vrtulemi a prosklená příď. */
function drawBomberPlane(ctx: CanvasRenderingContext2D, x: number, y: number, vx: number, time: number) {
  const dir = vx < 0 ? -1 : 1;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(dir, 1);

  // Křídla (velká křídla bombardéru za trupem)
  ctx.fillStyle = '#4c5847';
  ctx.beginPath();
  ctx.moveTo(-10, -18);
  ctx.lineTo(8, -2);
  ctx.lineTo(0, 3);
  ctx.lineTo(-18, -14);
  ctx.closePath();
  ctx.fill();

  // Spodní křídlo (pohled shora/z boku)
  ctx.beginPath();
  ctx.moveTo(-8, 16);
  ctx.lineTo(8, 2);
  ctx.lineTo(0, -1);
  ctx.lineTo(-16, 13);
  ctx.closePath();
  ctx.fill();

  // Trup letadla (kamufláž vojenská šedozelená)
  ctx.fillStyle = '#3f493b';
  ctx.beginPath();
  ctx.ellipse(0, 0, 24, 6.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // Spodní pumovnice
  ctx.fillStyle = '#262d24';
  ctx.beginPath();
  ctx.roundRect(-8, 1.5, 17, 3.5, 1.5);
  ctx.fill();

  // Dva motory na křídlech s točícími se vrtulemi
  for (const my of [-9, 8]) {
    ctx.fillStyle = '#262d24';
    ctx.beginPath();
    ctx.roundRect(2, my - 2.5, 10, 5, 1.5);
    ctx.fill();
    // Točící se vrtule (disk)
    const propPhase = time * 70;
    ctx.fillStyle = `rgba(240, 245, 255, ${0.45 + 0.35 * Math.sin(propPhase)})`;
    ctx.beginPath();
    ctx.ellipse(12, my, 1.4, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    // Žluté konce listů vrtule
    ctx.fillStyle = '#ffd23f';
    ctx.fillRect(11.5, my - 6.5, 1.2, 2);
    ctx.fillRect(11.5, my + 4.5, 1.2, 2);
  }

  // Ocasní plochy (zdvojená směrovka)
  ctx.fillStyle = '#394135';
  ctx.beginPath();
  ctx.moveTo(-20, -5);
  ctx.lineTo(-14, 0);
  ctx.lineTo(-20, 5);
  ctx.lineTo(-24, 0);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#262d24';
  ctx.fillRect(-22, -9, 3.5, 18);

  // Kokpit a prosklená příď bombometčíka!
  ctx.fillStyle = '#81ecec';
  ctx.beginPath();
  ctx.ellipse(17, -1, 4.5, 3, 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillRect(18, -2, 2.5, 1.5);

  ctx.restore();
}

/** Těžká letecká puma. */
function drawBomberBomb(ctx: CanvasRenderingContext2D, x: number, y: number, heading: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(heading);
  // Tělo bomby
  ctx.fillStyle = '#2d3436';
  ctx.beginPath();
  ctx.ellipse(0, 0, 8.5, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  // Žlutý proužek na špičce
  ctx.fillStyle = '#fdcb6e';
  ctx.fillRect(2, -3.4, 2, 6.8);
  // Křidélka vzadu
  ctx.fillStyle = '#636e72';
  ctx.fillRect(-8.5, -4.5, 3.2, 9);
  ctx.strokeStyle = '#2d3436';
  ctx.lineWidth = 0.8;
  ctx.strokeRect(-8.5, -4.5, 3.2, 9);
  // Roznětka
  ctx.fillStyle = '#d63031';
  ctx.fillRect(7.5, -1, 2, 2);
  ctx.restore();
}

function drawBombsight(
  ctx: CanvasRenderingContext2D,
  b: { x: number; y: number; planeX: number; planeY: number; bombsLeft: number },
  time: number,
) {
  const { x, y, planeX, planeY, bombsLeft } = b;
  ctx.save();

  // Spojovací naváděcí paprsek z letadla
  ctx.strokeStyle = 'rgba(46, 213, 115, 0.45)';
  ctx.lineWidth = 1.4;
  ctx.setLineDash([6, 6]);
  ctx.beginPath();
  ctx.moveTo(planeX, planeY + 8);
  ctx.lineTo(x, y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Zaměřovač na zemi
  ctx.translate(x, y);

  const pulse = Math.sin(time * 6) * 1.5;
  const rOuter = 20 + pulse;
  const rInner = 8;

  // Vnější kruh
  ctx.strokeStyle = '#2ed573';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(0, 0, rOuter, 0, Math.PI * 2);
  ctx.stroke();

  // Vnitřní kruh
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(0, 0, rInner, 0, Math.PI * 2);
  ctx.stroke();

  // Nitkový kříž (+)
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-rOuter - 7, 0); ctx.lineTo(-rInner - 2, 0);
  ctx.moveTo(rInner + 2, 0); ctx.lineTo(rOuter + 7, 0);
  ctx.moveTo(0, -rOuter - 7); ctx.lineTo(0, -rInner - 2);
  ctx.moveTo(0, rInner + 2); ctx.lineTo(0, rOuter + 7);
  ctx.stroke();

  // Zaměřovací rohy (L-brackets)
  const bSize = 6;
  const bOff = rOuter + 4;
  ctx.beginPath();
  ctx.moveTo(-bOff, -bOff + bSize); ctx.lineTo(-bOff, -bOff); ctx.lineTo(-bOff + bSize, -bOff);
  ctx.moveTo(bOff - bSize, -bOff); ctx.lineTo(bOff, -bOff); ctx.lineTo(bOff, -bOff + bSize);
  ctx.moveTo(-bOff, bOff - bSize); ctx.lineTo(-bOff, bOff); ctx.lineTo(-bOff + bSize, bOff);
  ctx.moveTo(bOff - bSize, bOff); ctx.lineTo(bOff, bOff); ctx.lineTo(bOff, bOff - bSize);
  ctx.stroke();

  // Středový červený bod
  ctx.fillStyle = '#ff4757';
  ctx.beginPath();
  ctx.arc(0, 0, 2.5, 0, Math.PI * 2);
  ctx.fill();

  // Popisek zaměřovače
  ctx.font = '700 10px monospace, system-ui';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.fillRect(-45, rOuter + 7, 90, 16);
  ctx.fillStyle = '#2ed573';
  ctx.fillText(`BOMBY: ${bombsLeft} ks`, 0, rOuter + 19);

  ctx.restore();
}

/**
 * Optický bombardovací zaměřovač (pohled z kabiny bombometčíka – kruhová optika typu Norden / Lotfernrohr).
 * Přesně odpovídá předloze s dělenou stupnicí, dálkovými značkami a vinetací.
 * Vykresluje se shora přes 3D scénu.
 */
export function drawBomberOpticOverlay(
  ctx: CanvasRenderingContext2D,
  vw: number,
  vh: number,
  bombsLeft: number,
  wind: number,
  time: number,
) {
  // Střed zaměřovače v obrazovkových souřadnicích (střed obrazovky)
  const cx = Math.round(vw / 2);
  const cy = Math.round(vh / 2);
  const r = Math.round(Math.min(vw, vh) * 0.44);

  ctx.save();

  // 1. Tmavá maska vně optiky (tubus periskopu)
  ctx.fillStyle = '#06080b';
  ctx.beginPath();
  ctx.rect(0, 0, vw, vh);
  ctx.arc(cx, cy, r, 0, Math.PI * 2, true);
  ctx.fill();

  // 2. Měkká vinetace čočky (ztmavení okrajů skla)
  const grad = ctx.createRadialGradient(cx, cy, r * 0.72, cx, cy, r);
  grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
  grad.addColorStop(0.65, 'rgba(0, 0, 0, 0.22)');
  grad.addColorStop(0.9, 'rgba(4, 6, 9, 0.75)');
  grad.addColorStop(1, 'rgba(6, 8, 11, 0.98)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // 3. Kovová obruba a odlesk čočky
  ctx.strokeStyle = 'rgba(230, 240, 215, 0.55)';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(180, 210, 240, 0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 3, 0, Math.PI * 2);
  ctx.stroke();

  // Jemný reflex optiky vlevo nahoře
  const glare = ctx.createLinearGradient(cx - r, cy - r, cx, cy);
  glare.addColorStop(0, 'rgba(255, 255, 255, 0.06)');
  glare.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = glare;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 2, Math.PI * 0.9, Math.PI * 1.6);
  ctx.lineTo(cx, cy);
  ctx.closePath();
  ctx.fill();

  // Oříznutí pro retikl – vše uvnitř čočky
  ctx.beginPath();
  ctx.arc(cx, cy, r - 2, 0, Math.PI * 2);
  ctx.clip();

  // Styl osnovy zaměřovače (světlá patina optického skla s tmavým stínem pro kontrast)
  ctx.shadowColor = 'rgba(0, 0, 0, 0.75)';
  ctx.shadowBlur = 2.5;
  ctx.strokeStyle = '#e6edd4';
  ctx.fillStyle = '#e6edd4';
  ctx.lineWidth = 1.2;

  // 4. Vertikální osa (celá výška kruhu)
  ctx.beginPath();
  ctx.moveTo(cx, cy - r + 4);
  ctx.lineTo(cx, cy + r - 4);
  ctx.stroke();

  // 5. Horizontální osa se stupnicí (podle předlohy se značením 0, 1 nahoře a 0, 2 dole)
  const scaleW = r * 0.62;
  ctx.beginPath();
  ctx.moveTo(cx - scaleW, cy);
  ctx.lineTo(cx + scaleW, cy);
  ctx.stroke();

  const step = r * 0.095;
  ctx.font = '700 9px monospace, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Popis středu 0 / 0
  ctx.fillText('0', cx + 7, cy - 10);
  ctx.fillText('0', cx + 7, cy + 11);

  // Značky a čísla vpravo a vlevo
  for (let i = 1; i <= 6; i++) {
    const xR = cx + i * step;
    const xL = cx - i * step;

    // Hlavní dílek
    ctx.beginPath();
    ctx.moveTo(xR, cy - 7); ctx.lineTo(xR, cy + 7);
    ctx.moveTo(xL, cy - 7); ctx.lineTo(xL, cy + 7);
    ctx.stroke();

    // Čísla nad osou (1, 2, 3...)
    const numTop = String(i);
    ctx.fillText(numTop, xR + (i === 1 ? 6 : 0), cy - 10);
    ctx.fillText(numTop, xL, cy - 10);

    // Čísla pod osou (2, 4, 6...)
    const numBot = String(i * 2);
    ctx.fillText(numBot, xR + (i === 1 ? 6 : 0), cy + 11);
    ctx.fillText(numBot, xL, cy + 11);

    // Poloviční mezidílky
    if (i < 6) {
      const halfR = cx + (i + 0.5) * step;
      const halfL = cx - (i + 0.5) * step;
      ctx.beginPath();
      ctx.moveTo(halfR, cy - 3.5); ctx.lineTo(halfR, cy + 3.5);
      ctx.moveTo(halfL, cy - 3.5); ctx.lineTo(halfL, cy + 3.5);
      ctx.stroke();
    }
  }

  // 6. Boční naváděcí hranice a dálkoměrné rysky (10° / 0°)
  const bX1 = cx - r * 0.44;
  const bX2 = cx + r * 0.44;
  const bYTop = cy - r * 0.38;
  const bYBot = cy + r * 0.28;

  ctx.beginPath();
  // Svislé vodící čáry
  ctx.moveTo(bX1, bYTop); ctx.lineTo(bX1, bYBot);
  ctx.moveTo(bX2, bYTop); ctx.lineTo(bX2, bYBot);
  // Spodní horizontální spojnice
  ctx.moveTo(bX1, bYBot); ctx.lineTo(bX2, bYBot);
  ctx.stroke();

  // Rysky na svislých vodících linkách s popisem úhlu
  const y10 = cy - r * 0.23;
  ctx.beginPath();
  ctx.moveTo(bX1 - 6, y10); ctx.lineTo(bX1 + 6, y10);
  ctx.moveTo(bX2 - 6, y10); ctx.lineTo(bX2 + 6, y10);
  ctx.moveTo(bX1 - 6, cy); ctx.lineTo(bX1 + 6, cy);
  ctx.moveTo(bX2 - 6, cy); ctx.lineTo(bX2 + 6, cy);
  ctx.stroke();

  ctx.font = '600 8px monospace, system-ui';
  ctx.fillText('10°', bX1 - 14, y10);
  ctx.fillText('10°', bX2 + 14, y10);
  ctx.fillText('0°', bX1 - 12, cy);
  ctx.fillText('0°', bX2 + 12, cy);

  // Drobné rysky na spodní spojnici
  for (let sX = bX1 + step; sX < bX2; sX += step) {
    ctx.beginPath();
    ctx.moveTo(sX, bYBot - 4);
    ctx.lineTo(sX, bYBot + 4);
    ctx.stroke();
  }

  // 7. Středový záměrný bod
  ctx.fillStyle = '#ff4757';
  ctx.beginPath();
  ctx.arc(cx, cy, 2.2, 0, Math.PI * 2);
  ctx.fill();

  // 8. Autentické instrumentální popisky zaměřovače
  ctx.shadowColor = 'transparent';
  ctx.font = '700 11px monospace, system-ui, sans-serif';
  ctx.fillStyle = '#dceec4';
  ctx.textAlign = 'center';
  ctx.fillText('BOMBOVÝ ZAMĚŘOVAČ · LOTFE 7 / NORDEN M-9', cx, cy - r * 0.78);

  ctx.font = '600 10px monospace, system-ui, sans-serif';
  ctx.fillStyle = '#c5dbad';
  const windDir = wind > 0 ? `→ +${wind.toFixed(1)}` : wind < 0 ? `← ${wind.toFixed(1)}` : '0.0';
  ctx.fillText(`VÝŠKA: 350 m · RYCHLOST: 220 km/h · VÍTR: ${windDir} m/s`, cx, cy - r * 0.70);

  // Spodní status munice
  const ready = bombsLeft > 0;
  ctx.fillStyle = ready ? '#2ed573' : '#ff4757';
  ctx.font = '700 11px monospace, system-ui, sans-serif';
  ctx.fillText(`💣 ZBÝVÁ BOMB: ${bombsLeft} / 3`, cx, cy + r * 0.68);

  ctx.fillStyle = 'rgba(230, 240, 215, 0.85)';
  ctx.font = '600 10px monospace, system-ui, sans-serif';
  ctx.fillText(ready ? '[MEZERNÍK / KLIK = SHODIT BOMBU]' : 'NÁLET DOKONČEN', cx, cy + r * 0.76);

  ctx.restore();
}
